/**
 * useProfileQueries — React Query wrappers for fetching user profiles.
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ProfilesState } from './useProfilesState';
import { debugLog } from '@/utils/debug';
import { logger } from '@/utils/logger';

export function useProfileQueries(state: ProfilesState) {
  const { 
    setProfiles, 
    setLoading, 
    setError, 
    setFetchedIds 
  } = state;

  // Fetch profiles for user IDs
  const fetchUserProfiles = useCallback(async (userIds: string[]) => {
    if (!userIds || userIds.length === 0) {
      debugLog("[useProfileQueries] No valid user IDs to fetch");
      return;
    }
    
    setLoading(true);
    
    try {
      debugLog("[useProfileQueries] Fetching profiles for user IDs:", userIds);
      
      // First set fallback display names for all new users
      const formattedProfiles: Record<string, { id: string }> = {};
      userIds.forEach(id => {
        formattedProfiles[id] = { id };
      });
      
      // Set these fallback profiles immediately
      setProfiles(prev => ({ ...prev, ...formattedProfiles }));
      
      // Fetch profiles via secure RPC (cross-user safe; only public fields)
      const { data, error } = await supabase
        .rpc('get_public_profiles', { _user_ids: userIds });
      
      if (error) {
        logger.error('[useProfileQueries] Error fetching profiles:', error);
        setError(error.message);
        return;
      }
      
      debugLog("[useProfileQueries] Profile data fetched from supabase:", data);
      
      if (data && data.length > 0) {
        // Create a map of profiles
        const profilesMap: Record<string, any> = {};
        data.forEach(profile => {
          profilesMap[profile.id] = profile;
          debugLog(`[useProfileQueries] Profile for ${profile.id}:`, profile);
        });
        
        // Update profiles state with fetched data
        setProfiles(prev => ({ ...prev, ...profilesMap }));
      } else {
        debugLog("[useProfileQueries] No profile data returned from supabase");
      }
      
      // Mark these IDs as fetched
      setFetchedIds(prev => {
        const newSet = new Set(prev);
        userIds.forEach(id => newSet.add(id));
        return newSet;
      });
    } catch (err: any) {
      logger.error('[useProfileQueries] Error in fetchUserProfiles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [setProfiles, setLoading, setError, setFetchedIds]);

  return {
    fetchUserProfiles
  };
}
